<template>
    <div :class="[$style['mds-pagination'], classObject, sizeClass]" :data-mds-version="version">
        <div :class="[$style['mds-pagination__left']]">
            <div :class="[$style['mds-pagination__page-select']]">
                {{ labelsMerged.page }}
                <mds-select
                    ref="pageSelect"
                    :class="[$style['mds-pagination__select']]"
                    v-model="pageSelected"
                    :on-dark="onDark"
                    label="page select"
                    hidden-label
                    :options="pageOptions"
                    :size="size"
                />
                {{ labelsMerged.of }} {{ totalPages }}
            </div>
            <div :class="[$style['mds-pagination__buttons']]">
                <mds-button-container>
                    <mds-button
                        ref="prevButton"
                        :text="labelsMerged.prev"
                        :on-dark="onDark"
                        :disabled="onFirstPage"
                        icon="angle-left"
                        :variation="iconOnlyButton"
                        :size="size"
                        @click="prevPage"
                    />
                    <mds-button
                        ref="nextButton"
                        :text="labelsMerged.next"
                        :disabled="onLastPage"
                        :on-dark="onDark"
                        :icon="iconOnly ? 'angle-right' : ''"
                        :icon-right="iconOnly ? '' : 'angle-right'"
                        :variation="iconOnlyButton"
                        :size="size"
                        @click="nextPage"
                    />
                </mds-button-container>
            </div>
            <div v-if="showItemsInfo" :class="[$style['mds-pagination__viewing-info']]">
                {{ itemsInfoText }}
            </div>
        </div>
        <div :class="[$style['mds-pagination__right']]">
            <div :class="[$style['mds-pagination__show-items']]" v-if="showItemsSelect">
                {{ labelsMerged.show }}
                <mds-select
                    ref="sizeSelect"
                    :class="[$style['mds-pagination__select']]"
                    label="row count select"
                    hidden-label
                    :on-dark="onDark"
                    :options="pageSizeOptions"
                    v-model="itemsSelected"
                    :size="size"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import MdsSelect from '@mds/select';
    import { MdsButton, MdsButtonContainer } from '@mds/button';
    import defaultLabels from './pagination_labels.json';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsPagination',
        props: {
            iconOnly: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'When true changes the Previous and Next page buttons to be icon-only.',
                },
            },
            labels: {
                type: Object,
                default: () => defaultLabels,
                docs: {
                    validation: '—',
                    description:
                        'Sets the label text used on the component. Individual labels can be overridden on an as-needed basis and will be merged with the default labels.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            page: {
                type: Number,
                default: 1,
                docs: {
                    validation: '—',
                    description: 'Sets the currently selected page.',
                },
            },
            pageSize: {
                type: Number,
                docs: {
                    validation: '—',
                    description: 'Sets number of items per page. Must match one of the values in `pageSizes`.',
                },
            },
            pageSizes: {
                type: Array,
                default: () => [10, 20, 50, -1],
                docs: {
                    validation: '—',
                    description: 'Sets the options in the show items per page select. `-1` displays all items.',
                },
            },
            showItemsInfo: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'When true, displays details describing the items currently shown and the total number of items paginated.',
                },
            },
            showItemsSelect: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'When true, displays the select that allows a user to set the number of items per page.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: '—',
                    description: 'Sets the size of the pagination.',
                },
            },
            totalItems: {
                type: Number,
                required: true,
                docs: {
                    validation: '—',
                    description: 'Sets the total number of items being paginated.',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
                docs: {
                    validation: 'Read-only',
                    description:
                        'Set automatically by package version, do not manually adjust this prop. Used to add a data attribute providing the component version to the component markup rendered in the DOM.',
                },
            },
        },
        components: {
            MdsButton,
            MdsButtonContainer,
            MdsSelect,
        },
        computed: {
            sizeClass() {
                return this.$style[`mds-pagination--${this.size}`];
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            onFirstPage() {
                return parseInt(this.pageSelected) === 1;
            },
            onLastPage() {
                return parseInt(this.pageSelected) === this.totalPages;
            },
            pageOptions() {
                const options = [];
                const numPages = this.totalPages;
                for (let i = 1; i <= numPages; i++) {
                    options.push(i);
                }
                return this.convertArrayOptions(options);
            },
            totalPages() {
                return Math.ceil(this.totalItems / this.rowCount);
            },
            pageSizeOptions() {
                return this.convertArrayOptions(this.pageSizes);
            },
            iconOnlyButton() {
                return this.iconOnly ? 'icon-only' : 'flat';
            },
            lastItem() {
                return this.rowCount * parseInt(this.pageSelected) > this.totalItems
                    ? this.totalItems
                    : this.rowCount * parseInt(this.pageSelected);
            },
            firstItem() {
                let pageFirst = this.rowCount * (parseInt(this.pageSelected) - 1);
                pageFirst = pageFirst + 1 > this.totalItems ? pageFirst : pageFirst + 1;

                return parseInt(this.pageSelected) > 1 ? pageFirst : 1;
            },
            rowCount() {
                return parseInt(this.itemsSelected) === -1 || this.itemsSelected > this.totalItems
                    ? this.totalItems
                    : this.itemsSelected;
            },
            labelsMerged() {
                return Object.assign({}, defaultLabels, this.labels);
            },
            itemsInfoText() {
                const itemsInfo = this.labelsMerged.itemsInfo || defaultLabels.itemsInfo;
                return itemsInfo
                    .replace('{{first}}', this.firstItem)
                    .replace('{{last}}', this.lastItem)
                    .replace('{{total}}', this.totalItems);
            },
        },
        data() {
            return {
                pageSelected: this.page,
                itemsSelected: this.pageSizes.includes(this.pageSize) ? this.pageSize : this.pageSizes[0],
            };
        },
        watch: {
            itemsSelected() {
                this.pageSelected = 1;
                if (this.pageSelected === 1) {
                    this.emitPageChangeEvent();
                }
            },
            pageSelected() {
                this.emitPageChangeEvent();
            },
            page(newPage) {
                this.pageSelected = newPage;
            },
        },
        methods: {
            convertArrayOptions(arr) {
                const optArray = [];
                // page size options array converted to options for select
                arr.forEach(element => {
                    const opt = {
                        text: element === -1 ? this.labelsMerged.all : element,
                        value: element,
                    };
                    optArray.push(opt);
                });

                return optArray;
            },
            emitPageChangeEvent() {
                this.$emit('mds-pagination-page-changed', {
                    page: parseInt(this.pageSelected),
                    pageSize: parseInt(this.itemsSelected),
                    firstItem: this.firstItem,
                    lastItem: this.lastItem,
                });
            },
            prevPage() {
                this.$emit('mds-pagination-to-prev');
                if (!this.onFirstPage) {
                    this.pageSelected = parseInt(this.pageSelected) - 1;
                }
            },
            nextPage() {
                this.$emit('mds-pagination-to-next');
                if (!this.onLastPage) {
                    this.pageSelected = parseInt(this.pageSelected) + 1;
                }
            },
        },
    };
</script>

<style lang="scss" module>
    @import './pagination.scss';
</style>
